import type { Ref } from 'vue'
import html2canvas from 'html2canvas-pro'
import jsPDF from 'jspdf'
import { getCurrentInstance, ref } from 'vue'

// A4 size in mm
const PDF_WIDTH = 297
const PDF_HEIGHT = 210

const LATERAL_PADDING = 10
const VERTICAL_PADDING = 10

export interface PdfDetailsItem {
  label: string
  content: string
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

interface PdfOptions {
  filename: string
  details: Array<PdfDetailsItem>
  onDocumentClone: (clonedDocument: Document) => void | Promise<void>
}

export function useHtml2Pdf(): {
  creatingPdf: Ref<boolean>
  downloadPdfWithDetails: (targetElement: string, options: PdfOptions) => void
} {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const creatingPdf = ref<boolean>(false)

  function downloadPdfWithDetails(targetElement: string, options: PdfOptions): void {
    processPdf(
      targetElement,
      getPdfFileNameWithDate(options.filename),
      options.onDocumentClone,
      options.details,
    )
  }

  async function processPdf(
    targetElement: string,
    name: string,
    onclone?: (clonedDocument: Document) => void | Promise<void>,
    details?: Array<PdfDetailsItem>,
  ): Promise<void> {
    creatingPdf.value = true

    const element = document.getElementsByClassName(targetElement)[0] as HTMLElement

    await html2canvas(element, {
      allowTaint: true,
      useCORS: true,
      scale: 2,
      onclone,
    }).then(canvas => {
      const pdf = createPdfFromCanvas(canvas)
      pdf.setFontSize(10)

      if (details) {
        printDetails(pdf, details)
      }

      pdf.save(name)
    })

    creatingPdf.value = false
  }

  function getPdfFileNameWithDate(name: string): string {
    const date = new Date()
      .toLocaleDateString(ctx?.proxy.$epb.locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short',
      })
      .replace(/, /g, '-')
      .replace(/ /g, '')
    return getPdfFileName(`${name}-${date}`)
  }

  function getPdfFileName(name: string): string {
    return `${name}.pdf`
  }

  function createPdfFromCanvas(canvas: HTMLCanvasElement): jsPDF {
    const imgData = canvas.toDataURL('image/jpeg')

    const imgWidth = canvas.width
    const imgHeight = canvas.height

    const ratio = Math.min(
      (PDF_WIDTH - 2 * LATERAL_PADDING) / imgWidth,
      (PDF_HEIGHT - 2 * VERTICAL_PADDING) / imgHeight,
    )

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    })

    const xPos = LATERAL_PADDING + (PDF_WIDTH - 2 * LATERAL_PADDING - imgWidth * ratio) / 2
    const yPos = VERTICAL_PADDING + (PDF_HEIGHT - 2 * VERTICAL_PADDING - imgHeight * ratio) / 2

    pdf.addImage(imgData, 'JPEG', xPos, yPos, imgWidth * ratio, imgHeight * ratio)

    return pdf
  }

  function printDetails(pdf: jsPDF, details: Array<PdfDetailsItem>): void {
    details.forEach(detailsItem => {
      if (!detailsItem.content) {
        return
      }

      const { xPos, yPos } = calculatePosition(pdf, detailsItem)
      drawItem(pdf, detailsItem, xPos, yPos)
    })
  }

  function calculatePosition(
    pdf: jsPDF,
    detailsItem: PdfDetailsItem,
  ): { xPos: number; yPos: number } {
    let xPos = LATERAL_PADDING
    let yPos = VERTICAL_PADDING

    switch (detailsItem.position) {
      case 'top-right':
        xPos =
          PDF_WIDTH -
          LATERAL_PADDING -
          pdf.getTextWidth(`${detailsItem.label}: ${detailsItem.content}`)
        break
      case 'bottom-left':
        yPos = PDF_HEIGHT - VERTICAL_PADDING
        break
      case 'bottom-right':
        xPos =
          PDF_WIDTH -
          LATERAL_PADDING -
          pdf.getTextWidth(`${detailsItem.label}: ${detailsItem.content}`)
        yPos = PDF_HEIGHT - VERTICAL_PADDING
        break
    }

    return { xPos, yPos }
  }

  function drawItem(pdf: jsPDF, detailsItem: PdfDetailsItem, xPos: number, yPos: number): void {
    pdf.setFont('helvetica', 'bold')

    if (detailsItem.label) {
      pdf.text(`${detailsItem.label}:`, xPos, yPos)
      xPos += pdf.getTextWidth(`${detailsItem.label}: `)
      pdf.setFont('helvetica', 'normal')
    }
    pdf.text(detailsItem.content, xPos, yPos)
  }

  return {
    creatingPdf,
    downloadPdfWithDetails,
  }
}
